import {
  DASHBOARD_RANGES,
  type DashboardData,
  type DashboardRange,
  formatMoney,
} from '@c214/shared';
import type { ReactNode } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { VisitsChart } from '../components/admin/VisitsChart';
import { Button } from '../components/ui/button';
import { Chip } from '../components/ui/chip';
import { useAdminDashboard } from '../hooks/useAdminDashboard';
import { useAuth } from '../hooks/useAuth';
import { useAuthModal } from '../providers';

const numberFormatter = new Intl.NumberFormat('uk-UA');
const formatCount = (value: number) => numberFormatter.format(value).replace(/ /g, ' ');

function parseRange(value: string | null): DashboardRange {
  return DASHBOARD_RANGES.some((r) => r.key === value) ? (value as DashboardRange) : '24h';
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border border-line bg-surface p-6">
      <h2 className="mono-label m-0 mb-4">{title}</h2>
      {children}
    </section>
  );
}

function BigNumber({ value, caption }: { value: string; caption: string }) {
  return (
    <>
      <p className="m-0 text-[clamp(28px,4vw,40px)] font-extrabold tracking-[-0.03em] text-ink">
        {value}
      </p>
      <p className="m-0 mt-2 text-[13px] text-muted">{caption}</p>
    </>
  );
}

function visitsMessage(data: DashboardData): string {
  return data.visitsError ? 'Не вдалося отримати дані Google Analytics' : 'Аналітика не підключена';
}

export function AdminDashboardPage() {
  const { data: user, isLoading: authLoading } = useAuth();
  const authModal = useAuthModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const range = parseRange(searchParams.get('range'));
  const { data, isLoading, isError } = useAdminDashboard(range);

  if (authLoading) return <p className="section-pad mono-label">ЗАВАНТАЖЕННЯ…</p>;

  if (!user) {
    return (
      <div className="section-pad text-center">
        <p className="mb-4 text-[15px] text-muted">Увійдіть, щоб переглянути огляд.</p>
        <Button variant="accent" size="lg" onClick={() => authModal.open('login')}>
          УВІЙТИ
        </Button>
      </div>
    );
  }

  if (user.role !== 'admin') return <Navigate to="/" replace />;

  return (
    <div className="section-pad">
      <div className="mb-8 flex flex-wrap items-baseline justify-between gap-4">
        <h1 className="m-0 text-[clamp(32px,6vw,72px)] font-extrabold tracking-[-0.045em]">
          Огляд
        </h1>
        <div className="flex gap-2">
          {DASHBOARD_RANGES.map((option) => (
            <Chip
              key={option.key}
              active={range === option.key}
              onClick={() => setSearchParams(option.key === '24h' ? {} : { range: option.key })}
            >
              {option.label}
            </Chip>
          ))}
        </div>
      </div>

      {isLoading && <p className="mono-label">ЗАВАНТАЖЕННЯ…</p>}
      {isError && <p className="mono-label">Не вдалося завантажити огляд.</p>}
      {data && (
        <>
          <section className="mb-6 border border-line bg-surface p-6">
            <h2 className="mono-label m-0 mb-4">ВІДВІДУВАННЯ</h2>
            {data.visits ? (
              <>
                <VisitsChart buckets={data.visits.buckets} range={range} />
                <div className="mt-4 flex flex-wrap gap-6 text-[13px] text-muted">
                  <span className="flex items-center gap-2">
                    <span className="inline-block h-[10px] w-[10px] bg-accent" aria-hidden="true" />
                    відвідувачі: {formatCount(data.visits.totals.visitors)}
                  </span>
                  <span className="flex items-center gap-2">
                    <span
                      className="inline-block h-[10px] w-[10px] bg-muted-2"
                      aria-hidden="true"
                    />
                    перегляди: {formatCount(data.visits.totals.pageviews)}
                  </span>
                </div>
                <p className="m-0 mt-3 text-[12px] text-muted-2">
                  Дані Google Analytics оновлюються із затримкою до кількох годин.
                </p>
              </>
            ) : (
              <p className="m-0 py-10 text-center text-[15px] text-muted">{visitsMessage(data)}</p>
            )}
          </section>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Card title="ВІДВІДУВАЧІ">
              {data.visits ? (
                <BigNumber
                  value={formatCount(data.visits.totals.visitors)}
                  caption={`${formatCount(data.visits.totals.pageviews)} переглядів`}
                />
              ) : (
                <p className="m-0 text-[13px] text-muted">{visitsMessage(data)}</p>
              )}
            </Card>
            <Card title="ПОПУЛЯРНІ СТОРІНКИ">
              {data.visits && data.visits.topPages.length > 0 ? (
                <ul className="m-0 list-none space-y-2 p-0">
                  {data.visits.topPages.map((page) => (
                    <li key={page.path} className="flex justify-between gap-4 text-[14px]">
                      <span className="truncate text-ink" title={page.path}>
                        {page.label}
                      </span>
                      <span className="font-mono text-muted">{formatCount(page.views)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="m-0 text-[13px] text-muted">
                  {data.visits ? 'Ще немає переглядів.' : visitsMessage(data)}
                </p>
              )}
            </Card>
            <Card title="КОРИСТУВАЧІ">
              <BigNumber
                value={`+${formatCount(data.users.added)}`}
                caption={`всього ${formatCount(data.users.total)}`}
              />
            </Card>
            <Card title="ЗАМОВЛЕННЯ">
              <BigNumber
                value={`+${formatCount(data.orders.added)} · ${formatMoney(data.orders.addedRevenue)}`}
                caption={`всього ${formatCount(data.orders.total)} · ${formatMoney(data.orders.totalRevenue)}`}
              />
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
